// ═══════════════════════════════════════════════════════════════
// DonorSearch CSV Field Mapping
// Path: src/utils/donorSearchMapping.js
//
// Given a header row from a DonorSearch (or compatible) export,
// map each column to a known internal field. Tolerant of
// case/spacing/punctuation variations and minor column renames
// across DonorSearch product versions. Unknown columns are
// preserved in raw_import so no data is silently dropped.
// ═══════════════════════════════════════════════════════════════

import { parseCsvLine } from "./csvParser";

// Normalize a header to a lookup key: lowercase, strip punctuation,
// collapse whitespace. Handles common DonorSearch variants.
const normalizeHeader = (h) =>
  (h || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");

// Known DonorSearch (and common vendor) column → internal field.
// Multiple aliases per field are expected — add more as new
// variants appear in exports.
const FIELD_ALIASES = {
  // Identity (for matching, not stored in wealth profile)
  firstName: ["first name", "first", "given name", "fname"],
  lastName: ["last name", "last", "surname", "family name", "lname"],
  fullName: ["full name", "name", "donor name", "prospect name"],
  email: ["email", "email address", "primary email", "e mail"],
  city: ["city", "home city", "donor city", "mailing city"],
  state: ["state", "home state", "donor state", "mailing state"],

  // Capacity rating from the vendor
  capacityRating: [
    "capacity rating",
    "ds capacity rating",
    "donorsearch capacity rating",
    "giving capacity",
    "giving capacity rating",
    "capacity band",
    "ask rating",
  ],
  capacityScore: [
    "capacity score",
    "ds capacity score",
    "giving capacity score",
    "p2g score",
    "philanthropic score",
  ],

  // Real estate
  realEstateValue: [
    "real estate",
    "real estate value",
    "real estate total",
    "real estate assessed",
    "real estate assessed value",
    "total real estate",
    "property value",
    "re total value",
  ],

  // Public stock holdings
  stockValue: [
    "stock value",
    "securities",
    "public stock",
    "public stockholder",
    "sec stock value",
    "securities value",
    "stock holdings",
  ],

  // FEC political giving
  politicalGivingTotal: [
    "political giving",
    "total political giving",
    "fec total",
    "fec giving",
    "fec giving total",
    "political contributions",
    "political giving total",
  ],

  // Foundation / DAF affiliations
  foundationAffiliations: [
    "foundation",
    "foundations",
    "foundation affiliations",
    "private foundation",
    "foundation director",
    "foundation trustee",
    "foundation officer",
  ],

  // Business / board affiliations
  businessOwnership: [
    "business",
    "business ownership",
    "businesses",
    "business affiliations",
    "board affiliations",
    "corporate board",
    "insider",
    "company",
  ],
};

// Build a reverse lookup: normalized alias → internal field name
const ALIAS_LOOKUP = (() => {
  const map = {};
  Object.keys(FIELD_ALIASES).forEach((field) => {
    FIELD_ALIASES[field].forEach((alias) => {
      map[normalizeHeader(alias)] = field;
    });
  });
  return map;
})();

// Numeric fields — coerce strings like "$1,234,567" or "1.2M" to number
const NUMERIC_FIELDS = new Set([
  "capacityScore",
  "realEstateValue",
  "stockValue",
  "politicalGivingTotal",
]);

// Convert vendor-provided dollar strings to a number. Accepts:
//   "$1,234,567"  → 1234567
//   "$1.2M"       → 1200000
//   "2500"        → 2500
//   "n/a" / ""    → null
const coerceNumber = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s || /^(n\/a|none|--|-|unknown)$/i.test(s)) return null;

  // Handle abbreviated suffixes (K, M, B)
  const suffixMatch = s.match(/^(\$?)([\d,.]+)\s*([kmb])\s*$/i);
  if (suffixMatch) {
    const value = parseFloat(suffixMatch[2].replace(/,/g, ""));
    if (isNaN(value)) return null;
    const multiplier = { k: 1000, m: 1000000, b: 1000000000 }[
      suffixMatch[3].toLowerCase()
    ];
    return value * multiplier;
  }

  const cleaned = s.replace(/[$,\s]/g, "");
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
};

// ── Public API ───────────────────────────────────────────────────

// Parse a CSV text blob into { headers: string[], rows: object[] }
// where each row is the raw string dict keyed by the original header.
export const parseCsvText = (text) => {
  if (!text) return { headers: [], rows: [] };
  // Strip BOM if present
  const cleaned = text.replace(/^\ufeff/, "");
  const lines = cleaned
    .split(/\r?\n/)
    .filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = parseCsvLine(lines[0]);
  const rows = lines.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = cells[idx] != null ? cells[idx] : "";
    });
    return obj;
  });
  return { headers, rows };
};

// Given header row → { [originalHeader]: internalField | null }
export const buildHeaderMap = (headers) => {
  const map = {};
  headers.forEach((h) => {
    const key = normalizeHeader(h);
    map[h] = ALIAS_LOOKUP[key] || null;
  });
  return map;
};

// Map a single raw row to the structured wealth profile shape.
// Returns:
//   {
//     match: { firstName, lastName, fullName, email, city, state },
//     wealth: { capacityRating, capacityScore, realEstateValue, ... },
//     raw:   { ...everything the row contained, keyed by original header }
//   }
export const mapRow = (row, headerMap) => {
  const match = {};
  const wealth = {};
  const raw = {};

  Object.keys(row).forEach((header) => {
    const value = row[header];
    raw[header] = value;
    const field = headerMap[header];
    if (!field) return;

    // Identity fields → match bucket
    if (["firstName", "lastName", "fullName", "email", "city", "state"].includes(field)) {
      match[field] = (value || "").trim() || null;
      return;
    }

    // Wealth fields
    if (NUMERIC_FIELDS.has(field)) {
      wealth[field] = coerceNumber(value);
    } else if (field === "foundationAffiliations" || field === "businessOwnership") {
      // Stored as jsonb — empty string becomes []
      const trimmed = (value || "").trim();
      wealth[field] = trimmed ? [{ raw: trimmed }] : [];
    } else {
      wealth[field] = (value || "").trim() || null;
    }
  });

  return { match, wealth, raw };
};

// ── Matching helpers ─────────────────────────────────────────────
//
// Matching strategy (per blueprint): email first, then name + city
// fallback. Always return a confidence tier so the UI can flag
// weaker matches to the user.

const norm = (s) => (s || "").trim().toLowerCase();

export const matchRowToMember = (matchFields, members) => {
  const email = norm(matchFields.email);
  if (email) {
    const hit = members.find((m) => norm(m.email) === email);
    if (hit) return { member: hit, tier: "email" };
  }

  // Build a full-name candidate
  let first = norm(matchFields.firstName);
  let last = norm(matchFields.lastName);
  const full = norm(matchFields.fullName);
  if (!first && !last && full) {
    const parts = full.split(/\s+/);
    first = parts[0] || "";
    last = parts.slice(1).join(" ") || "";
  }
  if (!first || !last) return { member: null, tier: "none" };

  const city = norm(matchFields.city);

  // Tier: name + city = "name_city"; name alone = "name_only"
  const nameCityHit = members.find(
    (m) =>
      norm(m.firstName) === first &&
      norm(m.lastName) === last &&
      city &&
      norm(m.homeCity || m.city) === city
  );
  if (nameCityHit) return { member: nameCityHit, tier: "name_city" };

  const nameMatches = members.filter(
    (m) => norm(m.firstName) === first && norm(m.lastName) === last
  );
  if (nameMatches.length === 1) {
    return { member: nameMatches[0], tier: "name_only" };
  }
  if (nameMatches.length > 1) {
    return { member: null, tier: "ambiguous", candidates: nameMatches.length };
  }
  return { member: null, tier: "none" };
};

// ── Convenience: full pipeline (parse + map + match) ─────────────
// Used by the import panel to produce the dry-run preview.
export const previewImport = (csvText, members) => {
  const { headers, rows } = parseCsvText(csvText);
  const headerMap = buildHeaderMap(headers);
  const mapped = rows.map((row) => {
    const result = mapRow(row, headerMap);
    const matchResult = matchRowToMember(result.match, members);
    return {
      ...result,
      matchedMember: matchResult.member,
      matchTier: matchResult.tier,
      candidates: matchResult.candidates,
    };
  });

  const counts = {
    total: mapped.length,
    matchedEmail: mapped.filter((r) => r.matchTier === "email").length,
    matchedNameCity: mapped.filter((r) => r.matchTier === "name_city").length,
    matchedNameOnly: mapped.filter((r) => r.matchTier === "name_only").length,
    ambiguous: mapped.filter((r) => r.matchTier === "ambiguous").length,
    unmatched: mapped.filter((r) => r.matchTier === "none").length,
  };

  // Unknown headers are preserved in raw_import but flagged for the
  // admin's awareness
  const unknownHeaders = headers.filter((h) => !headerMap[h]);

  return {
    headers,
    headerMap,
    unknownHeaders,
    rows: mapped,
    counts,
  };
};
